import React, { useCallback, useState } from 'react';
import { TrendingUp, Save, RefreshCw, Download, AlertCircle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicParametersSection from './BasicParametersSection';
import AdvancedOptionsSection from './AdvancedOptionsSection';
import BenchmarkComparisonSection from './BenchmarkComparisonSection';
import TaxSettingsSection from './TaxSettingsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент основной формы калькулятора инфляции
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  inflationRatesByYear,
  categoryInflationMultipliers,
  benchmarkAssets,
  saveScenario
}) => {
  
  // Состояние для имени сохраняемого сценария
  const [saveScenarioName, setSaveScenarioName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Дополнительная логика в зависимости от изменённого поля
      if (name === 'showCustomRate' && !checked) {
        newState.customInflationRate = '';
      }
      
      if (name === 'compareToBenchmarks' && !checked) {
        newState.benchmarks = ['usd', 'gold', 'realEstate'];
      }
      
      if (name === 'considerTaxes' && !checked) {
        newState.incomeTaxRate = '13';
      }

      if (name === 'useAverageInflation') {
        if (checked && !prev.averageYears) {
          newState.averageYears = '5';
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      amount, startYear, endYear, customInflationRate, showCustomRate,
      useAverageInflation, averageYears, considerTaxes, incomeTaxRate
    } = formData;

    // Парсинг числовых значений
    const numAmount = parseFloatOrDefault(amount, -1);
    const numStartYear = parseIntOrDefault(startYear, -1);
    const numEndYear = parseIntOrDefault(endYear, -1);
    const numCustomRate = showCustomRate ? parseFloatOrDefault(customInflationRate, -1) : 0;
    const numAverageYears = useAverageInflation ? parseIntOrDefault(averageYears, -1) : 0;
    const numTaxRate = considerTaxes ? parseFloatOrDefault(incomeTaxRate, -1) : 0;

    // Валидация базовых полей
    if (numAmount <= 0) newErrors.amount = 'Сумма должна быть положительной.';
    if (!newErrors.amount && numAmount > 100000000000) newErrors.amount = 'Максимальная сумма 100 млрд ₽.';
    
    const currentYear = new Date().getFullYear();
    const earliestYear = Math.min(...Object.keys(inflationRatesByYear).map(Number));
    
    if (!isInteger(startYear) || numStartYear < earliestYear) {
      newErrors.startYear = `Год должен быть целым числом не раньше ${earliestYear}.`;
    }
    
    if (!isInteger(endYear) || numEndYear > currentYear + 30) {
      newErrors.endYear = `Год должен быть целым числом не позднее ${currentYear + 30}.`;
    }
    
    if (!newErrors.startYear && !newErrors.endYear && numStartYear >= numEndYear) {
      newErrors.endYear = 'Конечный год должен быть больше начального.';
    }

    // Валидация пользовательской ставки инфляции
    if (showCustomRate) {
      if (numCustomRate < 0) newErrors.customInflationRate = 'Ставка не может быть отрицательной.';
      if (!newErrors.customInflationRate && numCustomRate > 1000) newErrors.customInflationRate = 'Ставка не может быть более 1000%.';
    }

    // Валидация количества лет для средней инфляции
    if (useAverageInflation) {
      if (!isInteger(averageYears) || numAverageYears <= 0) {
        newErrors.averageYears = 'Введите целое положительное число лет.';
      }
      if (!newErrors.averageYears && numAverageYears > 50) {
        newErrors.averageYears = 'Максимальное количество лет для усреднения - 50.';
      }
    }

    // Валидация ставки налога
    if (considerTaxes) {
      if (numTaxRate < 0) newErrors.incomeTaxRate = 'Ставка налога не может быть отрицательной.';
      if (!newErrors.incomeTaxRate && numTaxRate > 100) newErrors.incomeTaxRate = 'Ставка налога не может быть более 100%.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, inflationRatesByYear, setErrors]);

  // Функция расчета влияния инфляции
  const calculateInflationEffect = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const initialAmount = parseFloatOrDefault(formData.amount);
        const startYear = parseIntOrDefault(formData.startYear);
        const endYear = parseIntOrDefault(formData.endYear);
        const calculationType = formData.calculationType;
        const itemCategory = formData.itemCategory;

        // Определяем инфляцию для каждого года
        const yearlyInflation = {};
        let cumulativeInflation = 1.0;
        const timeSeriesData = [];
        const multiplier = categoryInflationMultipliers[itemCategory] || 1.0;

        // Для пользовательской ставки инфляции
        const customRate = formData.showCustomRate 
          ? parseFloatOrDefault(formData.customInflationRate) / 100 
          : null;

        // Для средней инфляции
        let averageRate = null;
        if (formData.useAverageInflation) {
          const years = parseIntOrDefault(formData.averageYears);
          const currentYear = new Date().getFullYear();
          let sum = 0;
          let count = 0;
          
          for (let i = 0; i < years; i++) {
            const year = currentYear - i;
            if (inflationRatesByYear[year]) {
              sum += inflationRatesByYear[year];
              count++;
            }
          }
          
          averageRate = count > 0 ? sum / count / 100 : null;
        }

        // Рассчитываем инфляцию для каждого года
        for (let year = startYear; year <= endYear; year++) {
          let inflationRate;
          
          if (customRate !== null) {
            inflationRate = customRate;
          } else if (averageRate !== null) {
            inflationRate = averageRate;
          } else {
            inflationRate = (inflationRatesByYear[year] || 5) / 100; // По умолчанию 5% если нет данных
          }
          
          // Применяем множитель для конкретной категории товаров
          inflationRate = inflationRate * multiplier;
          
          yearlyInflation[year] = inflationRate;
          
          // Рассчитываем накопленную инфляцию
          cumulativeInflation *= (1 + inflationRate);
          
          const yearValue = calculationType === 'appreciation'
            ? initialAmount * cumulativeInflation
            : initialAmount / cumulativeInflation;
          
          timeSeriesData.push({
            year,
            value: yearValue,
            inflationRate: inflationRate * 100,
            cumulativeInflation: (cumulativeInflation - 1) * 100
          });
        }

        // Рассчитываем результаты на основе типа расчета
        let finalValue, changePercent, changeAbsolute;
        
        if (calculationType === 'appreciation') {
          // Расчет будущей стоимости с учетом инфляции
          finalValue = initialAmount * cumulativeInflation;
          changeAbsolute = finalValue - initialAmount;
          changePercent = (cumulativeInflation - 1) * 100;
        } else {
          // Расчет обесценивания покупательной способности
          finalValue = initialAmount / cumulativeInflation;
          changeAbsolute = initialAmount - finalValue;
          changePercent = (1 - 1/cumulativeInflation) * 100;
        }

        // Расчет значений для сравнительных активов
        const benchmarkResults = {};
        if (formData.compareToBenchmarks) {
          // Исторические данные для бенчмарков (упрощенные для примера)
          const benchmarkData = {
            usd: { 2000: 28.16, 2005: 28.78, 2010: 30.48, 2015: 61.24, 2020: 73.88, 2023: 91.25, 2024: 92.57 },
            eur: { 2000: 26.14, 2005: 34.19, 2010: 40.33, 2015: 68.34, 2020: 90.68, 2023: 98.57, 2024: 100.35 },
            gold: { 2000: 290, 2005: 513, 2010: 1410, 2015: 1160, 2020: 1895, 2023: 2032, 2024: 2330 },
            realEstate: { 2000: 100, 2005: 190, 2010: 240, 2015: 320, 2020: 390, 2023: 450, 2024: 470 },
            stocks: { 2000: 100, 2005: 280, 2010: 350, 2015: 480, 2020: 620, 2023: 810, 2024: 860 },
            deposits: { 2000: 100, 2005: 160, 2010: 210, 2015: 290, 2020: 350, 2023: 390, 2024: 410 }
          };

          // Интерполяция данных для промежуточных лет
          const interpolateValue = (data, year) => {
            const years = Object.keys(data).map(Number).sort((a, b) => a - b);
            
            // Если точное значение есть
            if (data[year]) return data[year];
            
            // Если год меньше минимального или больше максимального
            if (year <= years[0]) return data[years[0]];
            if (year >= years[years.length - 1]) return data[years[years.length - 1]];
            
            // Находим ближайшие годы
            let prevYear = years[0];
            let nextYear = years[years.length - 1];
            
            for (const dataYear of years) {
              if (dataYear <= year) prevYear = dataYear;
              if (dataYear >= year && nextYear > dataYear) nextYear = dataYear;
            }
            
            if (prevYear === nextYear) return data[prevYear];
            
            // Линейная интерполяция
            const ratio = (year - prevYear) / (nextYear - prevYear);
            return data[prevYear] + ratio * (data[nextYear] - data[prevYear]);
          };

          for (const asset of formData.benchmarks) {
            if (!benchmarkData[asset]) continue;
            
            const startValue = interpolateValue(benchmarkData[asset], startYear);
            const endValue = interpolateValue(benchmarkData[asset], endYear);
            const growthRate = endValue / startValue;
            
            // Рассчитываем стоимость инвестиции в актив
            const investmentValue = initialAmount * growthRate;
            
            benchmarkResults[asset] = {
              startValue,
              endValue,
              growthRate,
              investmentValue,
              performanceVsInflation: investmentValue / finalValue
            };
          }
        }

        // Настраиваем итоговые результаты
        const finalResults = {
          initialAmount,
          startYear,
          endYear,
          calculationType,
          finalValue,
          changeAbsolute,
          changePercent,
          cumulativeInflation: (cumulativeInflation - 1) * 100,
          yearlyInflation,
          timeSeriesData,
          multiplier,
          itemCategory,
          benchmarkResults
        };

        // Если учитываем налоги
        if (formData.considerTaxes) {
          const taxRate = parseFloatOrDefault(formData.incomeTaxRate) / 100;
          
          if (calculationType === 'appreciation') {
            const taxableAmount = Math.max(0, finalValue - initialAmount);
            const taxAmount = taxableAmount * taxRate;
            finalResults.taxAmount = taxAmount;
            finalResults.afterTaxValue = finalValue - taxAmount;
          } else {
            // Для обесценивания налоги не считаем
            finalResults.taxAmount = 0;
            finalResults.afterTaxValue = finalValue;
          }
        }

        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, inflationRatesByYear, categoryInflationMultipliers]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      amount: '100000',
      startYear: new Date().getFullYear() - 1,
      endYear: new Date().getFullYear(),
      customInflationRate: '',
      showCustomRate: false,
      calculationType: 'depreciation',
      includeChart: true,
      includeHistorical: true,
      compareToBenchmarks: false,
      benchmarks: ['usd', 'gold', 'realEstate'],
      considerTaxes: false,
      incomeTaxRate: '13',
      useAverageInflation: false,
      averageYears: '5',
      selectedRegion: 'russia',
      compoundingPeriods: '1',
      itemCategory: 'general'
    });
    setResults(null);
    setErrors({});
    setSaveScenarioName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results || !results.timeSeriesData || results.timeSeriesData.length === 0) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = ['Год', 'Инфляция (%)', 'Накопленная инфляция (%)', 'Стоимость'];
    
    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = results.timeSeriesData.map(item => {
      return [
        item.year,
        formatCsvNumber(item.inflationRate),
        formatCsvNumber(item.cumulativeInflation),
        formatCsvNumber(item.value)
      ].join(';');
    });

    // Добавим основную информацию в начало файла
    const calculationTypeText = results.calculationType === 'appreciation' 
      ? 'Удорожание с учетом инфляции' 
      : 'Обесценивание покупательной способности';
    
    const categoryText = results.itemCategory !== 'general' 
      ? `Категория товаров: ${results.itemCategory} (коэффициент ${results.multiplier})` 
      : 'Общая инфляция';

    const infoRows = [
      ['Калькулятор инфляции', ''],
      ['Сумма:', formatCsvNumber(results.initialAmount) + ' ₽'],
      ['Период:', `${results.startYear} - ${results.endYear}`],
      ['Тип расчета:', calculationTypeText],
      [categoryText, ''],
      ['Начальная стоимость:', formatCsvNumber(results.initialAmount) + ' ₽'],
      ['Конечная стоимость:', formatCsvNumber(results.finalValue) + ' ₽'],
      ['Изменение:', formatCsvNumber(results.changePercent) + '%'],
      ['Накопленная инфляция:', formatCsvNumber(results.cumulativeInflation) + '%'],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `инфляция_${results.startYear}-${results.endYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения сценария
  const handleSaveScenario = () => {
    if (showSaveDialog) {
      const savedResult = saveScenario(saveScenarioName || null);
      if (savedResult) {
        alert(`Сценарий "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveScenarioName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateInflationEffect();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-red-500" />
          Параметры расчета инфляции
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Информационный баннер */}
        <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 flex">
          <AlertCircle className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Данный калькулятор позволяет оценить влияние инфляции на покупательную способность денег или рассчитать удорожание товаров с учетом инфляции в России. 
              Используются официальные данные по инфляции Росстата.
            </p>
          </div>
        </div>

        {/* Основные параметры */}
        <BasicParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          inflationRatesByYear={inflationRatesByYear}
        />

        {/* Дополнительные параметры */}
        <AdvancedOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          categoryInflationMultipliers={categoryInflationMultipliers}
        />

        {/* Сравнение с другими активами */}
        {formData.compareToBenchmarks && (
          <BenchmarkComparisonSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors}
            benchmarkAssets={benchmarkAssets}
          />
        )}

        {/* Настройки налогообложения */}
        {formData.considerTaxes && (
          <TaxSettingsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors}
          />
        )}

        {/* Диалог сохранения сценария */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveScenarioName}
                onChange={(e) => setSaveScenarioName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveScenario}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<TrendingUp className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать инфляцию'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать данные (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveScenario}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить сценарий
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;